import { useRef, useState, useEffect, useCallback } from 'react';

// A row that scrolls sideways in the app's own look, not the browser's (the
// director, 20 Oct 2026): only when its contents do not fit -- then ‹ › arrow
// buttons, a thin indicator underneath for where you are, a slightly darker
// background and soft edges as the hint that there is more. The mouse wheel
// scrolls it sideways while the pointer is on it.

const TEAL = '#14b8a6', BORDER = '#2a3040', SUNK = '#161b22';

export default function ScrollStrip({ children, gap = 4, step = 0.7 }) {
  const ref = useRef(null);
  const [st, setSt] = useState({ over: false, left: false, right: false, frac: 1, pos: 0 });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const span = el.scrollWidth - el.clientWidth;
    const next = {
      over: span > 1,
      left: el.scrollLeft > 1,
      right: el.scrollLeft < span - 1,
      frac: el.scrollWidth ? Math.min(1, el.clientWidth / el.scrollWidth) : 1,
      pos: span > 0 ? el.scrollLeft / span : 0,
    };
    // Only when something changed: measuring after every render must not
    // start a render of its own.
    setSt(s => (s.over === next.over && s.left === next.left && s.right === next.right
      && Math.abs(s.frac - next.frac) < 0.001 && Math.abs(s.pos - next.pos) < 0.001 ? s : next));
  }, []);

  useEffect(() => { measure(); });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    // The wheel scrolls the strip sideways while the pointer is on it.
    const wheel = (e) => {
      if (el.scrollWidth <= el.clientWidth || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); el.removeEventListener('wheel', wheel); };
  }, [measure]);

  const go = (dir) => { const el = ref.current; if (el) el.scrollBy({ left: dir * el.clientWidth * step, behavior: 'smooth' }); };
  const arrow = (dir, on) => (
    <button type="button" onClick={() => go(dir)} disabled={!on} aria-label={dir < 0 ? 'Scroll left' : 'Scroll right'}
      style={{ flexShrink: 0, width: 24, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 7,
               background: on ? 'rgba(20,184,166,0.10)' : 'transparent', border: `1px solid ${on ? TEAL + '66' : BORDER}`,
               color: on ? TEAL : '#4b5563', cursor: on ? 'pointer' : 'default', padding: 0, transition: 'background 0.12s, color 0.12s' }}>
      <i className={`fa-solid fa-chevron-${dir < 0 ? 'left' : 'right'}`} style={{ fontSize: 10 }} />
    </button>
  );
  const fade = (side) => ({
    position: 'absolute', top: 0, bottom: 6, [side]: 0, width: 18, pointerEvents: 'none', zIndex: 1,
    background: `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, ${SUNK}, rgba(22,27,34,0))`,
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, flex: '0 1 auto' }}>
      <style>{'.mo-scrollstrip { scrollbar-width: none; } .mo-scrollstrip::-webkit-scrollbar { display: none; }'}</style>
      {st.over && arrow(-1, st.left)}
      <div style={{ position: 'relative', minWidth: 0, flex: '0 1 auto', background: st.over ? SUNK : 'transparent',
                    borderRadius: 8, padding: st.over ? '3px 4px 7px' : 0, border: st.over ? `1px solid ${BORDER}` : 'none' }}>
        {st.over && st.left && <div style={fade('left')} />}
        {st.over && st.right && <div style={fade('right')} />}
        <div ref={ref} className="mo-scrollstrip" onScroll={measure} style={{ display: 'flex', gap, overflowX: 'auto' }}>
          {children}
        </div>
        {st.over && (
          <div aria-hidden="true" style={{ position: 'absolute', left: 6, right: 6, bottom: 2, height: 2, borderRadius: 1, background: BORDER }}>
            <div style={{ position: 'absolute', top: 0, bottom: 0, borderRadius: 1, background: TEAL,
                          width: `${st.frac * 100}%`, left: `${st.pos * (1 - st.frac) * 100}%`, transition: 'left 0.08s linear' }} />
          </div>
        )}
      </div>
      {st.over && arrow(1, st.right)}
    </div>
  );
}
